import * as Yup from 'yup';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import LoadingButton from '@mui/lab/LoadingButton';
import DialogTitle from '@mui/material/DialogTitle';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import { toast } from 'react-toastify';
import { useState, useEffect } from 'react';
import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import HttpService from '../../../hooks/Https-services';
import { API_ROUTES, ROLE_IDS } from '../../../utils/constant';

type Props = {
  open: boolean;
  onClose: VoidFunction;
  currentShift?: any;
  teams: any;
  fetchUsersList: any;
};

export default function ShiftQuickUpdateForm({
  currentShift,
  teams,
  open,
  onClose,
  fetchUsersList,
}: Props) {
  const validationSchema = Yup.object().shape({
    shiftName: Yup.string().required('Shift name is required'),
    roleId: Yup.number().required('Role is required'),
    teamId: Yup.number().required('Team is required'),
  });
  const [feld, setFild] = useState<any>({
    shiftName: '',
    roleId: '',
    teamId: '',
    startTime: null,
    endTime: null,
  });

  useEffect(() => {
    if (currentShift) {
      setFild({
        ...feld,
        shiftName: currentShift?.shiftName || '',
        roleId: currentShift?.roleId || '',
        teamId: currentShift?.teamId || '',
        startTime: dayjs(currentShift?.startTime) || null,
        endTime: dayjs(currentShift?.endTime) || null,
      });
    }
  }, [currentShift]);

  const handleSubmit = async (values: any, { setSubmitting }: any) => {
    const info = {
      shiftName: values.shiftName,
      roleId: values.roleId,
      teamId: values.teamId,
      startTime: values.startTime
        ? dayjs(values.startTime).toISOString()
        : dayjs().startOf('day').toISOString(),
      endTime: values.endTime
        ? dayjs(values.endTime).toISOString()
        : dayjs().startOf('day').toISOString(),
    };
    try {
      await HttpService.patch(
        `${API_ROUTES.updateShift}/${currentShift.id}`,
        info
      );
      toast.success('Update Profile Successfully', {
        position: 'bottom-right',
      });
      onClose();
      fetchUsersList();
    } catch (error: any) {
      console.error(error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      fullWidth
      maxWidth={false}
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: { maxWidth: 720 },
      }}
    >
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <Formik
          initialValues={feld}
          enableReinitialize={true}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            isSubmitting,
            setFieldValue,
          }) => (
            <Form>
              <DialogTitle>Update Details</DialogTitle>
              <DialogContent>
                <Box
                  rowGap={3}
                  columnGap={2}
                  display="grid"
                  gridTemplateColumns={{
                    xs: 'repeat(1, 1fr)',
                    sm: 'repeat(2, 1fr)',
                  }}
                  sx={{ marginTop: '10px' }}
                >
                  <Field
                    name="shiftName"
                    as={TextField}
                    label="Shift Name"
                    variant="outlined"
                    fullWidth
                    margin="normal"
                    helperText={
                      touched.shiftName && errors.shiftName
                        ? errors.shiftName
                        : null
                    }
                    error={touched.shiftName && !!errors.shiftName}
                  />

                  <FormControl fullWidth margin="normal">
                    <InputLabel id="role-select-label">Role</InputLabel>
                    <Field
                      name="roleId"
                      as={Select}
                      labelId="role-select-label"
                      label="Role"
                      disabled
                      helperText={
                        touched.roleId && errors.roleId ? errors.roleId : null
                      }
                      error={touched.roleId && !!errors.roleId}
                    >
                      {Object.entries(ROLE_IDS).map(([key, id]) => (
                        <MenuItem key={id} value={id}>
                          {key.toLowerCase()}{' '}
                          {/* You can display the role name here */}
                        </MenuItem>
                      ))}
                    </Field>
                    <ErrorMessage
                      name="roleId"
                      component="div"
                      // style={{ color: 'red' }}
                    />
                  </FormControl>

                  <FormControl fullWidth margin="normal">
                    <InputLabel id="team-select-label">Teams</InputLabel>
                    <Field
                      name="teamId"
                      as={Select}
                      disabled
                      labelId="team-select-label"
                      label="Teams"
                      helperText={
                        touched.teamId && errors.teamId ? errors.teamId : null
                      }
                      error={touched.teamId && !!errors.teamId}
                    >
                      {teams.map((team: any) => (
                        <MenuItem key={team.id} value={team.id}>
                          {team.userTeamKey}
                        </MenuItem>
                      ))}
                    </Field>
                    <ErrorMessage
                      name="teamId"
                      component="div"
                      // style={{ color: 'red' }}
                    />
                  </FormControl>
                  <FormControl fullWidth margin="normal">
                    <TimePicker
                      label="Shift Start Time"
                      value={values.startTime}
                      onChange={(newValue) =>
                        setFieldValue(`startTime`, newValue)
                      }
                      ampm={false}
                      // sx={{ width: '100%' }}
                    />
                  </FormControl>

                  <FormControl fullWidth margin="normal">
                    <TimePicker
                      label="Shift End Time"
                      value={values.endTime}
                      onChange={(newValue) =>
                        setFieldValue(`endTime`, newValue)
                      }
                      ampm={false}
                      sx={{ width: '100%' }}
                    />
                  </FormControl>
                </Box>
              </DialogContent>

              <DialogActions>
                <Button
                  variant="outlined"
                  onClick={onClose}
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  Cancel
                </Button>
                <LoadingButton
                  type="submit"
                  variant="contained"
                  loading={isSubmitting}
                  disabled={
                    isSubmitting ||
                    !values.shiftName ||
                    !values.roleId ||
                    !values.teamId
                  }
                >
                  Update
                </LoadingButton>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </LocalizationProvider>
    </Dialog>
  );
}
